import { useId, useRef, type ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { useDialog } from '../../hooks/useDialog'
import { IconButton } from './IconButton'

export type SheetSide = 'right' | 'left' | 'center'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** Desktop placement. Mobile is always a bottom sheet. */
  side?: SheetSide
  className?: string
}

const DESKTOP: Record<SheetSide, string> = {
  right: 'md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-[min(28rem,100%)] md:max-h-none md:rounded-none md:rounded-l-xl',
  left: 'md:inset-y-0 md:left-0 md:right-auto md:h-full md:w-[min(28rem,100%)] md:max-h-none md:rounded-none md:rounded-r-xl',
  center:
    'md:inset-0 md:m-auto md:h-fit md:max-h-[85vh] md:w-[min(40rem,100%)] md:rounded-xl',
}

/**
 * Bottom sheet on mobile, side panel (or centre panel) from md up. One element
 * moves between the two with breakpoint utilities rather than two trees, so the
 * focus trap and the scroll position survive a resize.
 */
export function Sheet({ open, onClose, title, children, footer, side = 'right', className }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDialog(open, onClose, panelRef)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-inverse-surface/40 backdrop-blur-sm"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'absolute inset-x-0 bottom-0 flex max-h-[90vh] flex-col rounded-t-xl border border-outline-variant/40 bg-surface-container-lowest outline-none',
          DESKTOP[side],
          className,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-primary/10 px-margin-mobile py-4 md:px-gutter">
          {/* The grabber reads as "draggable" on touch; it is decoration only. */}
          <div className="min-w-0 flex-1">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-outline-variant md:hidden" />
            {title && (
              <h2 id={titleId} className="truncate font-display text-headline-sm text-on-surface">
                {title}
              </h2>
            )}
          </div>
          <IconButton icon="close" label="Close" onClick={onClose} size="sm" className="mt-1" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-margin-mobile py-6 md:px-gutter">{children}</div>

        {footer && (
          <footer className="border-t border-primary/10 px-margin-mobile py-4 md:px-gutter">{footer}</footer>
        )}
      </div>
    </div>
  )
}
